import moment from 'moment';
import ExcelJS from 'exceljs';

const downloadChecklistEXCEL = async (listVal: any) => {
  if (!listVal?.Current_json) return;

  const data = JSON.parse(listVal.Current_json);

  // Helper function to check if a specific subsection should be shown
  const shouldShowSubSection = (section: any, showStatusKey: string): boolean => {
    if (!section || section[showStatusKey] === undefined) return true; // No show status, default to show if has data
    return section[showStatusKey] === true || section[showStatusKey] === "true";
  };

  // Helper function to check if a section has meaningful data
  const isMeaningfulValue = (value: any): boolean => {
    if (value === null || value === undefined) return false;
    if (typeof value === "boolean") return value === true;
    if (typeof value === "string") return value.trim() !== "";
    if (typeof value === "number") return true;
    if (Array.isArray(value)) return value.some(isMeaningfulValue);
    if (typeof value === "object") {
      // Special handling for objects with "value" property
      if ("value" in value) {
        return isMeaningfulValue(value.value) || value.verified === true;
      }
      return Object.values(value).some(isMeaningfulValue);
    }
    return false;
  };

  const isMeaningfulRow = (row: any): boolean => {
    if (!row || typeof row !== "object") return false;
    
    // Check for any meaningful value in the row
    for (const key in row) {
      if (isMeaningfulValue(row[key])) {
        return true;
      }
    }
    return false;
  };

  const hasMeaningfulRows = (rows: any[]): boolean => {
    if (!Array.isArray(rows)) return false;
    return rows.some(isMeaningfulRow);
  };

  const workbook = new ExcelJS.Workbook();
  const sheet = workbook.addWorksheet('Checklist');

  sheet.columns = [{ width: 45 }, { width: 45 }, { width: 45 }, { width: 45 }, { width: 45 }, { width: 45 }, { width: 45 }, { width: 45 }, { width: 45 }, { width: 45 }, { width: 45 }, { width: 45 }, { width: 45 }, { width: 45 }, { width: 45 }, { width: 45 }, { width: 45 }, { width: 45 }, { width: 45 }, { width: 45 }, { width: 45 }, { width: 45 }, { width: 45 }, { width: 45 }, { width: 45 }, { width: 45 }, { width: 45 }, { width: 45 }, { width: 45 }];
  sheet.getRow(1).hidden = true;

  // ==================== HELPERS ====================

  const normalizeColor = (c?: string): string => {
    if (!c) return 'FFFFFFFF';
    if (c === 'lightgreen') return 'FFD4EDDA';
    if (c.startsWith('#')) return 'FF' + c.replace('#', '').toUpperCase();
    return 'FFFFFFFF';
  };

  const addMergedRow = (text: string, bold = false, size = 12, bg?: string, height = 26) => {
    const row = sheet.addRow([text]);
    sheet.mergeCells(row.number, 1, row.number, 3);
    row.font = { bold, size };
    row.height = height;
    if (bg) {
      row.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: normalizeColor(bg) },
      };
    }
    return row;
  };

  const section = (title: string, bgColor?: string) =>
    addMergedRow(title, true, 13, bgColor);

  const subsection = (title: string) => {
    const row = sheet.addRow([title]);
    sheet.mergeCells(row.number, 1, row.number, 3);
    row.font = { bold: true };
    row.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFEEEEEE' } };
    row.height = 24;
    return row;
  };

  const field = (label: string, value: any) => {
    const isBool = typeof value === 'boolean';
    const display = isBool ? (value ? 'Yes' : 'No') : (value ?? '—');
    const status = isBool ? '●' : '';

    const row = sheet.addRow([label, display, status]);
    row.getCell(1).font = { bold: true };

    if (isBool) {
      row.getCell(3).font = {
        size: 16,
        bold: true,
        color: { argb: value ? 'FF28A745' : 'FFD32F2F' },
      };
      row.getCell(3).alignment = { horizontal: 'center' };
    }
  };

  const table = (title: string, items: any[], keys: string[], headers: string[]) => {
    if (!items || items.length === 0) return;

    subsection(title);
    const headerRow = sheet.addRow(headers);
    headerRow.eachCell((cell) => {
      cell.font = { bold: true };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE0E0E0' } };
    });

    let hasData = false;
    items.forEach((item) => {
      if (!item) return;
      const values = keys.map((k) =>
        typeof item[k] === 'boolean' ? (item[k] ? 'Yes' : 'No') : (item[k] ?? '')
      );
      if (values.some((v) => v !== '')) {
        sheet.addRow(values);
        hasData = true;
      }
    });

    if (!hasData) sheet.addRow(headers.map(() => ''));
    sheet.addRow([]); // spacing
  };

  const renderRepeatedBlock = (
    baseTitle: string,
    items: any[] = [],
    fields: { label: string; key: string; fallbackKey?: string }[],
    placeholder?: Record<string, string>,
    showStatus?: boolean
  ) => {
    // Don't show if showStatus is explicitly false
    if (showStatus === false) return;
    
    // Don't show if no items and no placeholder
    if (items.length === 0 && !placeholder) return;

    const list = items.length > 0 ? items : [{}]; // fake one item for placeholder

    list.forEach((item, idx) => {
      const showIndex = items.length > 1 || (items.length === 0 && placeholder);
      const title = `${baseTitle}${showIndex ? ` ${idx + 1}` : ''}`;
      subsection(title);

      fields.forEach(({ label, key, fallbackKey }) => {
        const primaryValue = item?.[key];
        const hasPrimaryValue =
          primaryValue !== undefined &&
          primaryValue !== null &&
          (typeof primaryValue !== "string" || primaryValue.trim() !== "");

        const value = items.length > 0
          ? (
              hasPrimaryValue
                ? primaryValue
                : fallbackKey
                  ? item?.[fallbackKey]
                  : undefined
            )
          : placeholder?.[key] ?? '—';
        field(label, value);
      });

      sheet.addRow([]); // spacing after block
    });
  };

  // Function to check if a section should be shown
  const shouldShowSection = (sectionKey: string): boolean => {
    const section = data[sectionKey];
    if (!section) return false;

    switch (sectionKey) {
      case "generalVerification":
        if (!shouldShowSubSection(section, "isshowgeneralVerificationStatus")) return false;
        return (
          isMeaningfulValue(section.isSpellingCorrect) ||
          isMeaningfulValue(section.ownerShip) ||
          (section.file && Object.values(section.file).some(isMeaningfulValue))
        );

      case "surveyNoVerification":
        const shouldShowRegister = shouldShowSubSection(section, "isshowregisterStatus");
        const shouldShowCurrentPatta = shouldShowSubSection(section, "isshowcurrentPattaStatus");
        const shouldShowTitleDeed = shouldShowSubSection(section, "isshowtitleDeedStatus");
        
        const hasRegisterData = shouldShowRegister ? hasMeaningfulRows(section.register || []) : false;
        const hasCurrentPattaData = shouldShowCurrentPatta ? hasMeaningfulRows(section.currentPatta || []) : false;
        const hasTitleDeedData = shouldShowTitleDeed ? hasMeaningfulRows(section.titleDeed || []) : false;
        
        const hasOtherData = 
          isMeaningfulValue(section.totalTitleDeed) ||
          isMeaningfulValue(section.totalEc) ||
          isMeaningfulValue(section.difference) ||
          isMeaningfulValue(section.missingEc) ||
          section.type === true ||
          section.conditionPatta === true;
        
        return hasRegisterData || hasCurrentPattaData || hasTitleDeedData || hasOtherData;

      case "deed":
        const deedTypes = [
          { key: "saleDeed", showStatus: "isshowsaleDeedStatus" },
          { key: "giftDeed", showStatus: "isshowgiftDeedStatus" },
          { key: "settlementDeed", showStatus: "isshowsettlementDeedStatus" },
          { key: "partitionDeed", showStatus: "isshowpartitionDeedStatus" },
          { key: "willDeed", showStatus: "isshowwillDeedStatus" },
          { key: "leaseDeed", showStatus: "isshowleaseDeedStatus" },
          { key: "attorneyDeed", showStatus: "isshowattorneyDeedStatus" },
          { key: "releaseDeed", showStatus: "isshowreleaseDeedStatus" },
          { key: "rectificationDeed", showStatus: "isshowrectificationDeedStatus" },
          { key: "consentDeed", showStatus: "isshowconsentDeedStatus" },
          { key: "missingDocument", showStatus: "isshowmissingDocumentStatus" },
        ];
        
        let hasAnyDeedData = false;
        
        for (const deedType of deedTypes) {
          if (shouldShowSubSection(section, deedType.showStatus)) {
            if (hasMeaningfulRows(section[deedType.key] || [])) {
              hasAnyDeedData = true;
              break;
            }
          }
        }
        
        const hasPlotRegularization = shouldShowSubSection(section, "ishowplotregulationstatus") &&
          section.plotRegularization &&
          (section.plotRegularization.isDeedRegisteredAfter30_9_2016 === true ||
            section.plotRegularization.isPlotObtained === true);
const hasGazetta =
            shouldShowSubSection(section, "ishowGazettestatus") &&
            section.gazetteofDoc &&
            (section.gazetteofDoc.isNameChanged === true ||
              section.gazetteofDoc.gazettePublication === true ||
              section.gazetteofDoc.NameChangedbythasildar === true ||
              section.gazetteofDoc.IndementyBond === true ||
              section.gazetteofDoc.docList !== "");

        const hasForeignPower = shouldShowSubSection(section, "ishowAdjudicationstatus") &&
          section.foriegnPower &&
          (section.foriegnPower.isForiegnPowerRegistered === true ||
            isMeaningfulValue(section.foriegnPower.docNo));

        const hasMortgageInPowerDoc = section.mortgageInPowerDoc === true;

        return (
          hasAnyDeedData ||
          hasPlotRegularization ||
          hasGazetta ||
          hasForeignPower ||
          hasMortgageInPowerDoc
        );

      case "HsdpattasVerify":
        if (!shouldShowSubSection(section, "isshowHsdpattasStatus")) return false;
        return hasMeaningfulRows(section.Hsdpattas || []);

      case "nathamPattaverify":
        if (!shouldShowSubSection(section, "isshownathamPattaStatus")) return false;
        return hasMeaningfulRows(section.nathamPatta || []);

      case "legalHeirsVerify":
        if (!shouldShowSubSection(section, "isshowlegalHeirsStatus")) return false;
        return hasMeaningfulRows(section.legalHeirs || []);

      case "ecVerify":
        // ecVerify doesn't have show status in your data, so always check data
        return hasMeaningfulRows(section.ecentry || []);

      case "flow":
        if (!shouldShowSubSection(section, "isshowflowStatus")) return false;
        return hasMeaningfulRows(section.flow || []);

      case "propertytax":
        if (!shouldShowSubSection(section, "isshowpropertytaxStatus")) return false;
        return hasMeaningfulRows(section.propertytax || []);

      case "EBreceiptData":
        if (!shouldShowSubSection(section, "isshowEBreceiptStatus")) return false;
        return hasMeaningfulRows(section.EBreceipt || []);

      case "missingLOD":
        return hasMeaningfulRows(section.sheets || []);

      case "verification":
        const verificationTypes = [
          { key: "company", showStatus: "isshowcompanyStatus" },
          { key: "trust", showStatus: "isshowtrustStatus" },
          { key: "partnership", showStatus: "isshowpartnershipStatus" },
          { key: "association", showStatus: "isshowassociationStatus" },
          { key: "society", showStatus: "isshowsocietyStatus" },
          { key: "appartment", showStatus: "isshowappartmentStatus" },
        ];
        
        for (const type of verificationTypes) {
          if (shouldShowSubSection(section, type.showStatus)) {
            const subSection = section[type.key];
            if (subSection && Object.values(subSection).some(isMeaningfulValue)) {
              return true;
            }
          }
        }
        return false;

      case "courtDocVerification":
        const courtDocTypes = [
          { key: "partitionSuit", showStatus: "isshowPartitionSuitStatus" },
          { key: "declarationSuit", showStatus: "isshowDeclarationSuitStatus" },
          { key: "lokAdalat", showStatus: "isshowLokAdalatStatus" },
          { key: "specificSuit", showStatus: "isshowSpecificSuitStatus" },
          { key: "appeal", showStatus: "isshowAppealStatus" },
          { key: "courtAuctionPurchase", showStatus: "isshowCourtAuctionPurchaseStatus" },
          { key: "sarfesiProceeding", showStatus: "isshowSarfesiProceedingStatus" },
        ];
        
        if (shouldShowSubSection(section, "isshowCourtDocStatus")) {
          for (const type of courtDocTypes) {
            const subSection = section[type.key];
            if (subSection && Object.values(subSection).some(isMeaningfulValue)) {
              return true;
            }
          }
        }
        
        for (const type of courtDocTypes) {
          if (shouldShowSubSection(section, type.showStatus)) {
            const subSection = section[type.key];
            if (subSection && Object.values(subSection).some(isMeaningfulValue)) {
              return true;
            }
          }
        }
        return false;

      case "originalDocVerification":
      case "remarks":
        return Object.values(section).some(isMeaningfulValue);

      default:
        return Object.values(section).some(isMeaningfulValue);
    }
  };

  // ==================== HEADER ====================

  addMergedRow(`${listVal.title || 'Process'} Checklist`, true, 16, '#e3f2fd');
  addMergedRow(`File No : ${listVal.file_id}`);
  addMergedRow(`Bank Name : ${listVal.bank_name}`);
  addMergedRow(`Login at : ${moment(listVal.login_dt).format('DD-MM-YYYY hh:mm A')}`);
  addMergedRow(`Process Checklist Completed at : ${moment(listVal.date).format('DD-MM-YYYY hh:mm A')}`);
  addMergedRow(`Role : ${listVal.officerType}`);
  addMergedRow(`Updated by : ${listVal.downloadedBy}`);
  sheet.addRow([]);

  // ==================== GENERAL VERIFICATION ====================

  if (shouldShowSection("generalVerification")) {
    section('General Verification', data.generalVerification?.bgColor);
    field('Title Owner/ Property Owner Name Verified with Aadharcard Name?', data.generalVerification?.ownerAadharName?.verified);
    field('Borrower Name Verified with Aadharcard Name?', data.generalVerification?.borrowerAadharName?.verified);
    field('Co-Borrower Name / Co-Aplicant Name Verified with AadharCard Name?', data.generalVerification?.coBorrowerAadharName?.verified);
    field('Is The Spelling for Mr./Smt/Minor/Late/ Initial  Correct?', data.generalVerification?.isSpellingCorrect);
    field('Address of the Property - Hint about File Verified', data.generalVerification?.addressofProperty?.verified);
    field('Nature of the Property (Residential/Commercial/Agri/Industrial)', data.generalVerification?.natureofProperty?.verified);
    field('APP ID Verified', data.generalVerification?.AppIdverfy?.verified);
    field('Constitution', data.generalVerification?.ownerShip?.verified);
    field('Hint About File Verified', data.generalVerification?.hintAbtfile);
    field('Freehold/ LeaseHold', data.generalVerification?.freeRleasehold?.verified);
    sheet.addRow([]);

    subsection('File');
    const fileDocs = data.generalVerification?.file || {};
    const fileDocLabels: Record<string, string> = {
      btTransfer: 'BT Transfer',
      modAvailable: 'MOD Available',
      purchase: 'Purchase',
      saleAgreement: 'Sale Agreement',
      selfConstruction: 'New Building / Construction/Self Construction',
      proceedingApproval: 'Proceeding Approval/Approved Plan',
      new: 'Fresh Case',
      preDisbursmentverfy:'Pre disbursement Verified',
      postDisbursverfy:'Post disbursement Verified',
    };
    Object.keys(fileDocLabels).forEach((key) => {
      field(fileDocLabels[key], fileDocs[key]);
    });
    sheet.addRow([]);
  }

  // ==================== HSDP PATTA ====================

  if (shouldShowSection("HsdpattasVerify")) {
    section('HSD Patta Verification ( Title Deed)/Assignment Patta Verification', data.HsdpattasVerify?.bgColor);

    renderRepeatedBlock(
      'HSDP Patta',
      data.HsdpattasVerify?.Hsdpattas || [],
      [
        { label: 'Condition Fulfilled', key: 'conditionFullfilled' },
        { label: 'NOC for Tahsildar', key: 'nocforTahsildar' },
        { label: "HSD Patta Issued by", key: 'hsdpattaissuedby' },
        { label: 'Mention Condition', key: 'mentionCondition' },
        { label: 'Boundary Certificate', key: 'certificate' },
        { label: 'Road Approaches', key: 'roadApproachs' },
      ],
      { roadApproachs: '—', certificate: '—', nocforTahsildar: '—', conditionFullfilled: '—', mentionCondition: '—' }
    );
  }

  // ==================== NATHAM PATTA ====================

  if (shouldShowSection("nathamPattaverify")) {
    section('Natham Patta (Title Deed)', data.nathamPattaverify?.bgColor);

    renderRepeatedBlock(
      'Natham Patta',
      data.nathamPattaverify?.nathamPatta || [],
      [
        { label: 'Issued by Special Tahsildar', key: 'specialTahsildar' },
        { label: 'Boundary Certificate', key: 'boundaryCertificate' },
        { label: 'Road Approach', key: 'roadApproach' },
      ],
      { specialTahsildar: '—', boundaryCertificate: '—', roadApproach: '—' }
    );
  }

  // ==================== Flow PATTA ====================

  if (shouldShowSection("flow")) {
    section('Flow', data.flow?.bgColor);

    renderRepeatedBlock(
      'Flow',
      data.flow?.flow || [],
      [
        { label: "Chainlink is in Order- Favour and Excecutant is in flow", key: "chainlink" },
        { label: "in flow ,document holder expired means Legal Heir Certificate available", key: "inFlowdoc" },
        { label: "All the Legal Heirs signed in the next document", key: "legalHeirsSigndoc" },
        { label: "In flow , Court order comes -Verified", key: "courtOrderVerify" },
        { label: "In flow, POA Verified", key: "flowPoAVerfy" },
        { label: "In flow Extent compare with the origin document to title deed", key: "flowextentDeed" },
        { label: "In flow Survey Number compare with the origin document to title deed", key: "flowSurveynoDeed" },
      ],
      { chainlink: '—', inFlowdoc: '—', legalHeirsSigndoc: '—' , courtOrderVerify: '—', flowPoAVerfy: '—', flowextentDeed: '—', flowSurveynoDeed: '—' }
    );
  }

  // ==================== Property Tax ====================

  if (shouldShowSection("propertytax")) {
    section('Property Tax', data.propertytax?.bgColor);

    renderRepeatedBlock(
      'Property Tax',
      data.propertytax?.propertyTax || [],
      [
        { label: "Date Verified", key: "dataVerified" },
        { label: "Property Owner Name Verified", key: "propertOwnerVerify" },
        { label: "Amount  Verified", key: "propertyAmountverify" },
        { label: "Door.No Verified", key: "doorNoVerify" },
        { label: "Assessment No Verified", key: "assessmentVerifi"},
        { label: "Property Tax issued by Verified", key: "propertyTaxVerify"},
        { label: "Property Tax Verified with QR Code", key: "propertyTaxVerifyQrcode" },
        { label: "Attached Proof Submitted for Verified QR Code", key: "attachProofVerifyQrcode" },
      ],
      { dataVerified: '—', propertOwnerVerify: '—', propertyAmountverify: '—' , doorNoVerify: '—', assessmentVerifi: '—', propertyTaxVerify: '—', propertyTaxVerifyQrcode: '—', attachProofVerifyQrcode: '—' }
    );
  }

  // ==================== EB Receipt ====================

  if (shouldShowSection("EBreceiptData")) {
    section('EB Receipt', data.EBreceiptData?.bgColor);

    renderRepeatedBlock(
      'EB Receipt',
      data.EBreceiptData?.EBreceipt || [],
      [
        { label: "Date Verified", key: "dataVerified" },
        { label: "Property Owner Name Verified", key: "propertOwnerVerify" },
        { label: "Amount  Verified", key: "ebAmountverify" },
        { label: "EB Receipt issued by Verified", key: "receiptIssuedByVerify" },
      ],
      { dataVerified: '—', propertOwnerVerify: '—', ebAmountverify: '—' , receiptIssuedByVerify: '—' }
    );
  }

  // ==================== SURVEY NO VERIFICATION ====================

  if (shouldShowSection("surveyNoVerification")) {
    section('Survey No Verification', data.surveyNoVerification?.bgColor);

    // A Register from Patta Table - only if show status is true
    if (shouldShowSubSection(data.surveyNoVerification, "isshowregisterStatus") && 
        data.surveyNoVerification?.register && 
        data.surveyNoVerification.register.length > 0) {
      table(
        'A Register from Patta',
        data.surveyNoVerification.register,
        ['surveyNo', 'tslrNo', 'extent', 'name', 'aRegisterMeanscompare'],
        ['Survey No', 'TSLR No', 'Extent', 'Name', 'Origin A Register Means Compare with Title Deed Survey Number']
      );
    }

    // Title Deed Table - only if show status is true
    if (shouldShowSubSection(data.surveyNoVerification, "isshowtitleDeedStatus") && 
        data.surveyNoVerification?.titleDeed && 
        data.surveyNoVerification.titleDeed.length > 0) {
      const titleDeedData = data.surveyNoVerification.titleDeed;
      
      table(
        'Title Deed',
        titleDeedData,
        [
          'surveyNo', 'tslrNo', 'pattaExtractSno', 'extent', 'name', 'Corelation', 'proceedingpatta',
          'deedExtentverfy', 'roadAppBound', 'pathwayBound', 'ofbyBoundverfy',
          'boundverfyNorth', 'boundverfySouth', 'boundverfyEast', 'boundverfyWest',
          'northeastMeasur', 'southeastMeasur', 'eastnorthMeasur', 'westnorthMeasur',
          'measurementextentmatch', 'landTypeNanjai', 'NonAgrifromtashidar',
          'deedDoorNoverfy', 'deedplotNoverfy', 'deedlayoutnameverfy',
          'deedstreetnameverfy', 'newtalukVerified'
        ],
        [
          'New Survey Number Verified',
          'New Survey Number Verified with Patta Extract',
          'Patta Extract S.No',
          'Title Deed Extent (Sq.Mt, Sq.Ft, Cents) Verified',
          'Name',
          'Corelation Certificate Verified',
          'Proceeding of Patta Verified',
          'Title Deed Extent Verified With Patta Extract',
          'Road Approach Available in Boundaries',
          'Pathway Available in Boundaries',
          'Of /BY Boundaries direction Verified',
          'Boundary of North Verified',
          'Boundary of South Verified',
          'Boundary of East Verified',
          'Boundary of West Verified',
          'North-East West Measurements in Sq.Ft/Sq.Mt',
          'South-East West Measurements in Sq.Ft/Sq.Mt',
          'East-North South Measurements In Sq.Ft/Sq.Mt',
          'West-North South Measurements Sq.Ft/Sq.Mt',
          'Measurement Extent Matched with Title Deed Total Extent',
          'Land type is Nanjai (Yes Means Hold/Policy/Approval)',
          'Is Title Extent more than 20 Cents (Non Agri From Tahsildar)',
          'Door No Verified',
          'Plot No Verified',
          'Layout Name Verified',
          'Street Name Verified',
          'New Taluk Verified with Patta'
        ]
      );
    }

    // Current Patta/Chitta/TSLR No Table - only if show status is true
    if (shouldShowSubSection(data.surveyNoVerification, "isshowcurrentPattaStatus") && 
        data.surveyNoVerification?.currentPatta && 
        data.surveyNoVerification.currentPatta.length > 0) {
      table(
        ' Current Patta/Chitta/TSLR No',
        data.surveyNoVerification.currentPatta,
        ['surveyNo', 'tslrNo', 'pattaExtractSno', 'extent', 'name', 'governmentLandorNotverify'],
        ['Survey No', 'TSLR No', 'Patta Extract S.No', 'Extent', 'Name', 'Government Land or Not-Verified']
      );
    }

    // Additional Survey Fields - including all fields from PDF
    const surveyFields = [
      { label: 'Total Survey No in Title Deed', key: 'totalTitleDeed' },
      { label: 'Total Survey No in EC', key: 'totalEc' },
      { label: 'Patta Verified with QR Code', key: 'pattaqrcode' },
      { label: 'Attachment Proof Submitted for Verified QR Code', key: 'proofverifyqrcode' },
      { label: 'Patta Issued by Verified', key: 'pattaissuedverfy' },
      { label: 'Difference', key: 'difference' },
      { label: 'Missing Survey No in EC', key: 'missingEc' },
      {
        label: 'Land Type  (Yes Means Hold/Policy/Approval)',
        key: 'type',
        format: (v: any) => (v ? 'Nanjai' : 'Punjai'),
      },
      { label: 'Condition Patta', key: 'conditionPatta' },
    ];

    // Add fields only if they have values
    surveyFields.forEach(({ label, key, format }: any) => {
      const value = data.surveyNoVerification?.[key];
      
      // Check if value exists and is not empty
      if (value !== undefined && value !== null && value !== '') {
        // For boolean values, convert to Yes/No
        const displayValue = typeof value === 'boolean' ? (value ? 'Yes' : 'No') : value;
        field(label, format ? format(displayValue) : displayValue);
      }
    });

    sheet.addRow([]);
  }

  // ==================== DEED ====================

  if (shouldShowSection("deed")) {
    section('Deed', data.deed?.bgColor);

    const deedConfigs = [
      {
        title: 'Sale Deed',
        key: 'saleDeed',
        showStatus: shouldShowSubSection(data.deed, "isshowsaleDeedStatus"),
        fields: [
          { label: 'Amount Fully Transferred?', key: 'saleDeedAmountTransfered' },
          { label: 'Conditions Mentioned or Not?', key: 'saleDeedConditionsMentioned' }
        ],
      },
      {
        title: 'Gift Settlement Deed',
        key: 'giftDeed',
        showStatus: shouldShowSubSection(data.deed, "isshowgiftDeedStatus"),
        fields: [
          { label: 'Derived By', key: 'derivedBy' },
          { label: 'Registered?', key: 'registered' },
          { label: 'Condition?', key: 'condition' },
          { label: 'Condition Fulfilled?', key: 'conditionFulfilled' },
          { label: 'Mention Condition', key: 'mentionCondition' },
        ],
      },
      {
        title: 'Settlement Deed',
        key: 'settlementDeed',
        showStatus: shouldShowSubSection(data.deed, "isshowsettlementDeedStatus"),
        fields: [
          { label: 'Derived By', key: 'derivedBy' },
          { label: 'Registered?', key: 'registered' },
          { label: 'Condition?', key: 'condition' },
          { label: 'Condition Fulfilled?', key: 'conditionFulfilled' },
          { label: 'Death Certificate Obtained', key: 'deathcertificateobtained' },
          { label: 'Relationship Mentioned', key: 'relationshipMentioned' },
          { label: 'Mention Condition', key: 'mentionCondition' },
        ],
      },
      {
        title: 'Partition Release Deed',
        key: 'partitionDeed',
        showStatus: shouldShowSubSection(data.deed, "isshowpartitionDeedStatus"),
        fields: [
          { label: 'Derived By', key: 'derivedBy' },
          { label: 'Registered?', key: 'registered' },
          { label: 'Condition?', key: 'condition' },
          { label: 'Condition Fulfilled?', key: 'conditionFulfilled' },
          { label: 'Mention Condition', key: 'mentionCondition' },
          { label: 'Doc Alloted For Mortgage', key: 'partionscheduleMortage', fallbackKey: 'docAllotedForMortgage' },
          { label: 'Original Copy Available', key: 'originalCopyAvailable' },
        ],
      },
      {
        title: 'Will Deed',
        key: 'willDeed',
        showStatus: shouldShowSubSection(data.deed, "isshowwillDeedStatus"),
        fields: [
          { label: 'Derived By', key: 'derivedBy' },
          { label: 'Registered?', key: 'registered' },
          { label: 'Condition?', key: 'condition' },
          { label: 'Condition Fulfilled?', key: 'conditionFulfilled' },
          { label: 'Mention Condition', key: 'mentionCondition' },
          { label: 'Cerified Copy Verified', key: 'willdeedcertcopyverify' },
          { label: 'Death Certificate Obtained', key: 'willdeaddeathcert' },
          { label: 'Relationship Mentioned', key: 'willdeadreationship' },
        ],
      },
      {
        title: 'Lease Deed',
        key: 'leaseDeed',
        showStatus: shouldShowSubSection(data.deed, "isshowleaseDeedStatus"),
        fields: [
          { label: 'Lease Period From Date', key: 'leasePeriodFromDate' },
          { label: 'Lease Period To Date', key: 'leasePeriodToDate' },
          { label: 'NOC Available?', key: 'nocAvailable' },
          { label: "Is there a word  to empower mortgage in Lease Deed?", key: "leaseDeedempowermortage" },
          { label: "Lease Cancellation Available?", key: "leaseDeedCancellation" },
        ],
      },
      {
        title: 'Attorney Deed',
        key: 'attorneydeed',
        showStatus: shouldShowSubSection(data.deed, "isshowattorneyDeedStatus"),
        fields: [
          { label: 'Derived By', key: 'derivedBy' },
          { label: 'Registered?', key: 'registered' },
          { label: 'Is In Force?', key: 'isInForce' },
          { label: 'Is Certified Copy Available?', key: 'isCertifiedCopyAvailable' },
          { label: "Is there a word  to empower mortgage in power Document?", key: "attorneyDeedmortage" },
          { label: "Sale Receipt -Subsequent Deed available or not?", key: "attorneyDeedsaleReceipt" },
          { label: "Life Certificate is available for current Transfer(30 days Validity)", key: "attorneyLifecert" },
        ],
      },
      {
        title: 'Release Deed',
        key: 'releaseDeed',
        showStatus: shouldShowSubSection(data.deed, "isshowreleaseDeedStatus"),
        fields: [
          { label: 'Derived By', key: 'derivedBy' },
          { label: 'Registered?', key: 'registered' },
          { label: 'Condition?', key: 'condition' },
          { label: 'Fulfilled?', key: 'fulFilled' },
          { label: 'Property Share', key: 'propertyshare' },
          { label: 'Mention Condition', key: 'mentionCondition' },
          { label: "Death Certificate Obtained?", key: "deatcertficate" },
          { label: "Relationship Mentioned?", key: "relationShipmentioned" },
        ],
      },
      {
        title: 'Rectification Deed',
        key: 'rectificationDeed',
        showStatus: shouldShowSubSection(data.deed, "isshowrectificationDeedStatus"),
        fields: [
          { label: 'Derived By', key: 'derivedBy' },
          { label: 'Registered?', key: 'registered' },
          { label: 'Wrong Mentioned Details', key: 'mentionDetails' },
          { label: 'Correct Details', key: 'correctDetails' },
        ],
      },
      {
        title: 'Consent Deed',
        key: 'consentDeed',
        showStatus: shouldShowSubSection(data.deed, "isshowconsentDeedStatus"),
        fields: [
          { label: 'Derived By', key: 'derivedBy' },
          { label: 'Registered?', key: 'registered' },
          { label: 'Reason', key: 'reason' },
          { label: "Age checked with Aadharcard", key: "agecheckAadhar" },
        ],
      },
      {
        title: 'Missing Document',
        key: 'missingDocument',
        showStatus: shouldShowSubSection(data.deed, "isshowmissingDocumentStatus"),
        fields: [
          { label: "Police Complaint", key: "policeComplaint" },
          { label: "Paper Ad ( Tamil/ English)", key: "paperAd" },
          { label: "Non Tracable certificate by Police", key: "tracableCertificate" },
          { label: "No Claim Certificate by Advocate", key: "NoClaimcertificate" },
        ],
      },
    ];

    deedConfigs.forEach(({ title, key, showStatus, fields }) => {
      renderRepeatedBlock(title, data.deed?.[key], fields, undefined, showStatus);
    });

    if (shouldShowSubSection(data.deed, "ishowplotregulationstatus") && data.deed?.plotRegularization) {
      subsection('Plot Regularization');
      field('Is Deed Registered After 30-09-2016?', data.deed.plotRegularization.isDeedRegisteredAfter30_9_2016);
      field('Is Plot Regulation Obtained?', data.deed.plotRegularization.isPlotObtained);
      sheet.addRow([]);
    }
    
    if (shouldShowSubSection(data.deed, "ishowGazettestatus") && data.deed?.gazetteofDoc) {
      subsection('Gazette');
      field('Is Name Changed(Gazette)', data.deed.gazetteofDoc.isNameChanged);
      field('Gazette Publication available?', data.deed.gazetteofDoc.gazettePublication);
      field('Name Changed Certifcate by Tahsildar/VAO', data.deed.gazetteofDoc.NameChangedbythasildar);
      field('Indementy Bond/Affidavit', data.deed.gazetteofDoc.IndementyBond);
      field('Name Changed Document(s) List', data.deed.gazetteofDoc.docList);
      sheet.addRow([]);
    }

    if (shouldShowSubSection(data.deed, "ishowAdjudicationstatus") && (data.deed?.foreignPower || data.deed?.mortgageInPowerDoc)) {
      subsection('Adjudication Power Deed');
      field('Is Adjudication Power Registered??', data.deed.foreignPower?.isForeignPowerRegistered);
      field('Doc No', data.deed.foreignPower?.docNo);
      field('Is there a word to Empowerr to Mortgage ', data.deed?.mortgageInPowerDoc);
      sheet.addRow([]);
    }
  }

  // ==================== LEGAL HEIRS ====================

  if (shouldShowSection("legalHeirsVerify")) {
    section('Legal Heirs Verification', data.legalHeirsVerify?.bgColor);

    renderRepeatedBlock(
      'Legal Heir',
      data.legalHeirsVerify?.legalHeirs || [],
      [
        { label: 'Legal Heirs Within 13 Years Period?', key: 'legalHeirsWithin13' },
        { label: 'Legal Heirs Certificate Number & Year', key: 'docNoAndYear' },
        { label: 'Next Deed Doc No & Year', key: 'nextDeedDocNoAndYear' },
        { label: "Predeceased Son/Daughter Legal Heirs Verified", key: "predeceasedlegalHeire" },
        { label: "Legal Heir Certificate Verified with QR Code", key: "legalHeirsWithqrcode" },
        { label: "Attached Proof Submitted for Verified QR Code", key: "legalHeirsattacedproof" },
        { label: 'Legal Heirs Obtained From', key: 'legalHeirsObtained' },
        { label: 'Genealogy Obtained from', key: 'genealogy' },
      ],
      { legalHeirsWithin13: '—', docNoAndYear: '—', nextDeedDocNoAndYear: '—', legalHeirsObtained: '—', genealogy: '—' }
    );
  }

  // ==================== EC VERIFICATION ====================

  if (shouldShowSection("ecVerify")) {
    section('EC Verification', data.ecVerify?.bgColor);

    renderRepeatedBlock(
      'EC Entry',
      data.ecVerify?.ecentry || [],
      [
        { label: 'No of EC Entries', key: 'noOfEcEntries' },
        { label: 'Related EC Entries', key: 'relatedEcEntries' },
        { label: 'Not Related Verified', key: 'notRelatedVerified' },
        { label: 'SRO EC Verified with QR Code', key: 'onlineEcVerified' },
        { label: 'Court Attachment', key: 'isCourtAttachment' },
        { label: 'Attachment Cancelled', key: 'isAttachmentCanceled' },
        { label: 'Lease in EC', key: 'leaseInEc' },
        { label: 'Lease Cancellation', key: 'leaseCancellation' },
        { label: 'Mortgage in EC', key: 'isMortgageInEc' },
        { label: 'Receipt In EC', key: 'isReceiptInEc' },
        { label: 'Agreement in EC', key: 'isAgreementInEc' },
        { label: 'Agreement Canceled', key: 'isAgreementCanceled' },
        { label: 'Larger Extent', key: 'largerExtent' },
        { label: 'Non Related S No', key: 'nonRelatedSNo' },
        { label: 'Different Plot No', key: 'differentPlotNo' },
        { label: "Property Details (S.No / Extent / Plot No) Verified", key: "propertdetailsverfy" },
        { label: "Period Continuity upto Date Verified", key: "periodcontinuityverfy" },
        { label: "Attached Proof Submitted for Verified QR Code", key: "attcheProofVerifyQrcode" },
        { label: "Same Plot No / Same Extent / Same S.No / or Title Holder or Previous Title Holder found in EC entries", key: "holderECentries" },
        { label: "Any Index Error Found", key: "indexerrorfound" },
        { label: "Double Entry (Document Number / Plot No / Extent)", key: "doubleEntry" },
        { label: "EC Page Missing Verified", key: "ecPageMIssing" }
      ],
      {
        noOfEcEntries: '—',
        relatedEcEntries: '—',
        notRelatedVerified: '—',
        onlineEcVerified: '—',
        isCourtAttachment: '—',
        isAttachmentCanceled: '—',
        leaseInEc: '—',
        leaseCancellation: '—',
        isMortgageInEc: '—',
        isReceiptInEc: '—',
        isAgreementInEc: '—',
        isAgreementCanceled: '—',
        largerExtent: '—',
        nonRelatedSNo: '—',
        differentPlotNo: '—',
        propertdetailsverfy: '—',
        periodcontinuityverfy: '—',
        attcheProofVerifyQrcode: '—',
        holderECentries: '—',
        doubleEntry: '—',
        ecPageMIssing: '—',
      }
    );
  }

  // ==================== MISSING LOD ====================

  if (shouldShowSection("missingLOD")) {
    section('Missing Sheets in LOD', data.missingLOD?.bgColor);

    const sheets = data.missingLOD?.sheets || [];
    if (sheets.length > 0) {
      subsection('Missing Sheets Details');

      sheets.forEach((item: any, idx: number) => {
        subsection(`Sheet ${idx + 1}`);
        field('Deed Name', item?.fileName);
        field('Doc No', item?.fileNo);
        field('Total Sheets', item?.totalSheets);
        field('Available Sheets', item?.availableSheets);
        field('Missing Sheet', item?.missingSheets);
        field('Is Certificate Copy Attached?', item?.isCertificateAttached ?? item?.isCopyAttached);
        field('Gazette Publication available', item?.gazettePublication);
        field('Name Changed Certifcate by Tahsildar/VAO', item?.NameChangedbythasildar);
        field('Indementy Bond/Affidavit', item?.IndementyBond);
        sheet.addRow([]);
      });
    }

    field('Is Name Changed(Gazette)?', data.missingLOD?.isNameChanged);
    field('Name Changed Document(s) List', data.missingLOD?.docList);
    sheet.addRow([]);
  }

  // ==================== VERIFICATION ====================

  if (shouldShowSection("verification")) {
    section('Verification', data.verification?.bgColor);

    const entityConfigs = [
      {
        title: 'Company',
        data: data.verification?.company,
        showStatus: shouldShowSubSection(data.verification, "isshowcompanyStatus"),
        fields: [
          { label: 'Board Resolution', key: 'boardResolution' },
          { label: 'All Directors Signed', key: 'allDirectorsSignedIn' },
          { label: ' Mortage Permit  in MOA & AOA ', key: 'MoaAndAoa' },
          { label: 'Cert of Incorporation Available', key: 'certOfInc' },
          { label: "Mortage Permit  in MOA & AOA ", key: "mortagepermit" },
        ],
      },
      {
        title: 'Trust',
        data: data.verification?.trust,
        showStatus: shouldShowSubSection(data.verification, "isshowtrustStatus"),
        fields: [
          { label: 'Trust Deed Available', key: 'isDeed' },
          { label: 'Supplimentary Trust Deed Available', key: 'isSupplement' },
          { label: 'Board of Resolution Signed', key: 'isResolutionSigned' },
          { label: 'Affidavit from all the Trustees Signed', key: 'isAffidavitSigned' },
        ],
      },
      {
        title: 'Partnership',
        data: data.verification?.partnership,
        showStatus: shouldShowSubSection(data.verification, "isshowpartnershipStatus"),
        fields: [
          { label: ' Partnership Deed Available', key: 'isPartnershipDeed' },
          { label: "Supplimentary Partnership Deed Available", key: "issupplimentaryPartnershipDeed" },
          { label: "Is there a word to Empower to Mortgage", key: "wordtoempowerrmortage" },
          { label: "Letter Obtained from Partners to Mortgage", key: "letterObtainpartenermortage" },
          { label: 'Form C', key: 'isFormC' },
          { label: 'Retire Partner', key: 'isRetirePartner' },
          { label: 'Add Of Partner', key: 'addOfPartner' },
          { label: 'Letter Obtained', key: 'letterObtained' },
          { label: 'Single Partner In Deed', key: 'singlePartnerInDeed' },
        ],
      },
      {
        title: 'Association',
        data: data.verification?.association,
        showStatus: shouldShowSubSection(data.verification, "isshowassociationStatus"),
        fields: [
          { label: 'Association Law', key: 'isAssociationLaw' },
          { label: 'Board of Resolution', key: 'isResolution' },
          { label: 'Power To Sign Execution', key: 'powerToSignExecution' },
        ],
      },
      {
        title: 'Society',
        data: data.verification?.society,
        showStatus: shouldShowSubSection(data.verification, "isshowsocietyStatus"),
        fields: [
          { label: 'MOA', key: 'isMOA' },
          { label: 'Society Registered', key: 'isSocietyRegistered' },
          { label: ' Board of Resolution Society Signed?', key: 'isResolutionSocietySigned' },
          { label: "Bylaw of Society?", key: "isBylaw" },
          { label: "Power to Mortgage and Sale?", key: "isMortagesale" },
        ],
      },
      {
        title: 'Apartment',
        data: data.verification?.appartment,
        showStatus: shouldShowSubSection(data.verification, "isshowappartmentStatus"),
        fields: [
          { label: 'UDS', key: 'isUDS' },
          { label: 'Floor No Verified', key: 'isBuildingFloor' },
          { label: 'Extent', key: 'isExtent' },
        ],
      },
    ];

    entityConfigs.forEach(({ title, data: entity, showStatus, fields }) => {
      if (showStatus && entity && Object.values(entity || {}).some((v) => v !== undefined)) {
        subsection(title);
        fields.forEach(({ label, key }) => field(label, entity?.[key]));
        sheet.addRow([]);
      }
    });
  }

  // ==================== COURT DOCUMENT VERIFICATION ====================

  if (shouldShowSection("courtDocVerification")) {
    section('Court Document Verification', data.courtDocVerification?.bgColor);

    const courtConfigs = [
      {
        title: 'Partition Suit',
        data: data.courtDocVerification?.partitionSuit,
        showStatus: shouldShowSubSection(data.courtDocVerification, "isshowPartitionSuitStatus"),
        fields: [
          { label: 'Final Decree', key: 'isFinalDecree' },
          { label: 'Preliminary Decree', key: 'isPreliminaryDecree' },
          { label: 'Compromise Decree', key: 'isCompormiseDecree' },
          { label: 'Exparty Decree', key: 'isExpartyDecree' },
          { label: "Contested Decree", key: "ContestedDecree" },
        ],
      },
      {
        title: 'Declaration Suit (Permanent/Temporary Injunction)',
        data: data.courtDocVerification?.declarationSuit,
        showStatus: shouldShowSubSection(data.courtDocVerification, "isshowDeclarationSuitStatus"),
        fields: [
          { label: 'Is IA Available?', key: 'isIAAvailable' },
          { label: 'Contest', key: 'isContest' },
          { label: 'Compromise Decree', key: 'isCompormiseDecree' },
          { label: 'Exparty Decree', key: 'isExpartyDecree' },
        ],
      },
      {
        title: 'Lok Adalat',
        data: data.courtDocVerification?.lokAdalat,
        showStatus: shouldShowSubSection(data.courtDocVerification, "isshowLokAdalatStatus"),
        fields: [
          { label: 'Old Court Name', key: 'oldCourtName' },
          { label: 'Case No', key: 'caseNo' },
          { label: 'Lok Adalat Case No', key: 'lokAdalatCaseNo' },
        ],
      },
      {
        title: 'Specific Performance Suit',
        data: data.courtDocVerification?.specificSuit,
        showStatus: shouldShowSubSection(data.courtDocVerification, "isshowSpecificSuitStatus"),
        fields: [
          { label: 'Agreement Holder Name', key: 'agreementHolderName' },
          { label: 'OS No', key: 'osNo' },
          { label: 'EP No', key: 'epNo' },
        ],
      },
      {
        title: 'Appeal',
        data: data.courtDocVerification?.appeal,
        showStatus: shouldShowSubSection(data.courtDocVerification, "isshowAppealStatus"),
        fields: [
          { label: 'Lower Court Name', key: 'lowerCourtName' },
          { label: 'Lower Court Case No', key: 'lowerCourtcaseNo' },
          { label: 'Appeal Court Name', key: 'appealCourtName' },
          { label: 'Appeal Case No', key: 'appealCaseNo' },
          { label: 'Is Lower Court', key: 'isLowerCourt' },
          { label: 'Lower Court  Judjment reversed', key: 'lowerCourtReverse' },
          { label: 'Lower Court  Judjement Confirm', key: 'alter' },
        ],
      },
      {
        title: 'Court Auction Purchase',
        data: data.courtDocVerification?.courtAuctionPurchase,
        showStatus: shouldShowSubSection(data.courtDocVerification, "isshowCourtAuctionPurchaseStatus"),
        fields: [
          { label: 'OS No', key: 'osNo' },
          { label: 'EP No', key: 'epNo' },
          { label: 'Bidder Name', key: 'bidderName' },
          { label: 'Property Holder Name', key: 'propertyHolderName' },
          { label: 'Bank Mortgage Involved?', key: 'isBankMortageInvolved' },
          { label: 'Mother Available?', key: 'isMotherAvailable' },
          { label: 'Sales Available?', key: 'isSalesAvailable' },
        ],
      },
      {
        title: 'SARFAESI Proceeding',
        data: data.courtDocVerification?.sarfesiProceeding,
        showStatus: shouldShowSubSection(data.courtDocVerification, "isshowSarfesiProceedingStatus"),
        fields: [
          { label: 'Possession  Notice Date(Period 60 day Expired)', key: 'possessionDate' },
          { label: 'Symbolic Possession Date (Period 60 day Expired)', key: 'symbolicPossession' },
          { label: 'Borrower Notice Date', key: 'borrowerNotice' },
          { label: 'Auction Date', key: 'auctionDate' },
          { label: 'Paid 15 Percent?', key: 'isPaid15Percent' },
          { label: 'Sales Certificate Obtained', key: 'isSalesCertificate' },
          { label: 'Registered', key: 'isRegistered' },
        ],
      },
    ];

    courtConfigs.forEach(({ title, data: entity, showStatus, fields }) => {
      if (showStatus && entity && Object.values(entity).some((v) => v !== undefined && v !== null)) {
        subsection(title);
        fields.forEach(({ label, key }) => field(label, entity[key]));
        sheet.addRow([]);
      }
    });
  }

  // ==================== Original DOCUMENT VERIFICATION ====================

  if (shouldShowSection("originalDocVerification")) {
    section('Original Document Verification', data.originalDocVerification?.bgColor);

    const OriginalConfigs = [
      {
        title: 'Original Document Verification',
        data: data.originalDocVerification,
        fields: [
          { label: "Title Deed Original availability", key: "titleDeedOriginalAvailable" },
          { label: "(N-1) Immediate parent document original availability", key: "immediateParentDocAvailable" },
          { label: "Last 13 Years document original availability", key: "last13YearsDocAvailable" },
          { label: "Same Extent document original availability", key: "sameExtentDocAvailable" },
          { label: "Larger Extent documents wrongly mentioned original in LOD", key: "largerExtentDocWronglyMentioned" },
          { label: "Pre Disbursement Verified", key: "preDisbursementVerified" },
          { label: "Post Disbursement Verified", key: "postDisbursementVerified" },
        ],
      },
    ];

    OriginalConfigs.forEach(({ title, data: entity, fields }) => {
      if (entity && Object.values(entity).some((v) => v !== undefined && v !== null)) {
        subsection(title);
        fields.forEach(({ label, key }) => field(label, entity[key]));
        sheet.addRow([]);
      }
    });
  }

  // ==================== REMARKS ====================

  if (data.remarks) {
    section('Remarks', '#fff3e0');
    addMergedRow(data.remarks || '-', false);
  }

  // ==================== EXPORT ====================

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });

  const fileName = `${listVal.file_id}_${listVal.title}_Checklist_${moment().format('DD-MM-YYYY_HHmm')}.xlsx`;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  a.click();
  URL.revokeObjectURL(url);
};

export default downloadChecklistEXCEL;